import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
  AuthUser,
  fetchAuthSession,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser as getCurrentUserAmplify,
  signOut
} from '@aws-amplify/auth';
import { useMsal } from '@azure/msal-react';
import {
  MobileDevice,
  Query,
  Subscription,
  SubscriptionOnMobileDeviceRegisteredByIdArgs,
  SubscriptionOnRegistrationTokenSavedByIdArgs
} from 'graphql/generated/graphqlTypes';
import { GET_CURRENT_VISITOR } from 'graphql/queries';
import { ON_MOBILE_DEVICE_REGISTERED_BY_ID, ON_REGISTRATION_TOKEN_SAVED_BY_ID } from 'graphql/subscriptions';
import { useEffect, useState } from 'react';
import { authenticationFailAction, authenticationSuccessAction, signOutAction } from 'state/actions/auth';
import { setLoadingTokens, triggerFetchToken } from 'state/actions/graphqlToken';
import { authVar, graphqlTokenVar } from 'state/vars';

const useAuth = () => {
  const { accounts, inProgress, instance } = useMsal();
  const { user, loading } = useReactiveVar(authVar);
  const { loading: tokenLoading, tokenType, isTriggerFetchToken } = useReactiveVar(graphqlTokenVar);
  const [loggedAccount] = accounts;
  const [cognitoUserData, setCognitoUserData] = useState<AuthUser>();
  const [cognitoUserAttributes, setCognitoUserAttributes] = useState<FetchUserAttributesOutput>();

  const [getCurrentVisitor, { error: userError, data, subscribeToMore, loading: loadingQuery }] =
    useLazyQuery<Pick<Query, 'getCurrentVisitor'>>(GET_CURRENT_VISITOR);
  const hostID = data?.getCurrentVisitor.id;
  const hostMobileDevice = data?.getCurrentVisitor.mobileDevices;

  useEffect(() => {
    if (user === null && loggedAccount !== undefined && tokenType === 'ENTRA') {
      getCurrentVisitor();
    }
  }, [user, loggedAccount, tokenType]);

  useEffect(() => {
    if (userError?.message === 'Visitor not found') {
      handleLogout();
    }
  }, [userError]);

  useEffect(() => {
    if (user === null && cognitoUserData !== undefined && tokenType === 'COGNITO') {
      getCurrentVisitor();
    }
  }, [user, cognitoUserData, tokenType]);

  const getCognitoUserData = async () => {
    setCognitoUserData(await getCurrentUserAmplify());
    setCognitoUserAttributes(await fetchUserAttributes());
  };

  useEffect(() => {
    if (!tokenLoading && tokenType === 'COGNITO') getCognitoUserData();
  }, [tokenLoading, tokenType, isTriggerFetchToken]);
  useEffect(() => {
    if (!loadingQuery && data && !tokenLoading) {
      const host = data.getCurrentVisitor;
      if (tokenType === 'COGNITO') {
        if (cognitoUserData) {
          authenticationSuccessAction({
            id: host.id,
            email: host.email || undefined,
            name: cognitoUserAttributes?.name || cognitoUserData.signInDetails?.loginId || cognitoUserData.username,
            isAdmin: host.isAdmin,
            isEntraLess: host.identityProvider === 'ENTRA' ? false : true,
            mobileDevices: host.mobileDevices
          });
        }
      } else if (tokenType === 'ENTRA') {
        if (accounts.length > 0 && loggedAccount) {
          const { name } = loggedAccount;
          authenticationSuccessAction({
            id: host.id,
            email: host.email || undefined,
            name: name || '',
            isAdmin: host.isAdmin,
            isEntraLess: host.identityProvider === 'ENTRA' ? false : true,
            mobileDevices: host.mobileDevices
          });
        }
      }
    }
  }, [data, cognitoUserData, tokenLoading, tokenType]);

  useEffect(() => {
    // The onboarding flow ('TENANT') has no authenticated visitor — it authenticates with the URL
    // registration JWT, not Cognito/Entra. Resolve auth loading the same way as the unauthenticated
    // ('NONE') case, otherwise authVar.loading stays true forever and Router is stuck on <Loading />.
    const hasNoAuthenticatedUser = tokenType === 'NONE' || tokenType === 'TENANT';
    if (inProgress === 'none' && !tokenLoading && !isTriggerFetchToken && hasNoAuthenticatedUser && user === null) {
      authenticationFailAction();
    }
  }, [inProgress, tokenLoading, isTriggerFetchToken, tokenType, user]);

  useEffect(() => {
    if (!subscribeToMore || !hostID) return;

    const unsubscribeMobileDeviceRegistered = subscribeToMore<
      Pick<Subscription, 'onMobileDeviceRegisteredById'>,
      SubscriptionOnMobileDeviceRegisteredByIdArgs
    >({
      document: ON_MOBILE_DEVICE_REGISTERED_BY_ID,
      variables: {
        visitorId: hostID
      },
      updateQuery: (prev, { subscriptionData: { data } }) => {
        if (!data) {
          return prev;
        }
        const newData = data.onMobileDeviceRegisteredById!;
        return {
          getCurrentVisitor: {
            ...prev.getCurrentVisitor,
            mobileDevices: [
              ...prev.getCurrentVisitor.mobileDevices.map((device) =>
                device.id === newData.mobileDeviceId
                  ? ({
                      ...device,
                      isRegistered: true,
                      registrationDate: newData.registrationDate,
                      registrationToken: null
                    } as MobileDevice)
                  : device
              )
            ]
          }
        };
      }
    });
    const unsubscribeRegistrationTokenSaved = subscribeToMore<
      Pick<Subscription, 'onRegistrationTokenSavedById'>,
      SubscriptionOnRegistrationTokenSavedByIdArgs
    >({
      document: ON_REGISTRATION_TOKEN_SAVED_BY_ID,
      variables: {
        visitorId: hostID
      },
      updateQuery: (prev, { subscriptionData: { data } }) => {
        if (!data) {
          return prev;
        }
        const newData = data.onRegistrationTokenSavedById!;
        return {
          getCurrentVisitor: {
            ...prev.getCurrentVisitor,
            mobileDevices: [
              ...prev.getCurrentVisitor.mobileDevices.map((device) =>
                device.role === 'ACCESS'
                  ? ({
                      ...device,
                      isRegistered: false,
                      id: newData.mobileDeviceId,
                      registrationToken: newData.registrationToken
                    } as MobileDevice)
                  : device
              )
            ]
          }
        };
      }
    });

    return () => {
      unsubscribeMobileDeviceRegistered();
      unsubscribeRegistrationTokenSaved();
    };
  }, [subscribeToMore, hostID]);

  const isMobileDeviceRegisteredWithAccess = (mobileDevices: MobileDevice[] = hostMobileDevice!) =>
    mobileDevices.some((mobileDevice) => mobileDevice.isRegistered && mobileDevice.role === 'ACCESS');

  const isWaitingForQrCodeValue = () => {
    if (
      hostMobileDevice?.some(
        (mobileDevices) =>
          mobileDevices.role === 'ACCESS' &&
          mobileDevices.isRegistered !== undefined &&
          mobileDevices.registrationToken !== undefined
      )
    )
      if (
        hostMobileDevice?.some(
          (mobileDevices) =>
            mobileDevices.role === 'ACCESS' && !mobileDevices.isRegistered && mobileDevices.registrationToken === null
        )
      )
        return true;
    return false;
  };

  const handleLogout = async () => {
    if (tokenType === 'ENTRA') {
      await instance.logout({ account: accounts[0], postLogoutRedirectUri: process.env.REACT_APP_SSO_REDIRECT_URI });
      signOutAction();
      setLoadingTokens();
    } else if (tokenType === 'COGNITO') {
      await signOut();
      const authSession = await fetchAuthSession();
      if (!authSession.credentials) {
        signOutAction();
        // Re-run derivation: getCognitoToken finds no session, clears the credential, derives NONE.
        triggerFetchToken();
      }
    }
  };

  // The app stays in a loading state until every step of sign-in has fully settled —
  // this prevents briefly showing "logged out" or "logged in" before all the data is ready.
  //  - inProgress !== 'none'      → MSAL itself is mid-flow. Only relevant to the Microsoft SSO login
  //                                  path (or MSAL's redirect handling on app boot); Cognito email/password
  //                                  sign-in never touches this, since MSAL is never invoked for it.
  //  - loading                    → authVar's user is still being resolved
  const getIsLoading = () =>
    inProgress !== 'none' ||
    loading ||
    loadingQuery ||
    isWaitingForQrCodeValue() ||
    tokenLoading ||
    isTriggerFetchToken ||
    ((tokenType === 'COGNITO' || tokenType === 'ENTRA') && user === null);

  return {
    handleLogout,
    isUserAuthenticated: Boolean(user),
    isLoading: getIsLoading(),
    isMobileDeviceRegisteredWithAccess
  };
};

export default useAuth;
